/**
 * Barge-in detection during SPEAKING (Segment V1.3): a SEPARATE AnalyserNode
 * from the LISTENING VAD's, on the same mic stream — deliberately not
 * reusing VoiceActivityDetector or its ambient calibration, because the
 * calibration target here is fundamentally different. LISTENING calibrates
 * against silence (room noise); SPEAKING calibrates against the device's
 * own TTS echo bleeding into the mic, which is louder and bursty (music/
 * speech peaks, not steady-state noise) — hence a high percentile (p90) of
 * the calibration window, not its mean.
 *
 * Recalibrate on every entry to SPEAKING — construct a fresh instance each
 * time, never reused across turns. Volume and environment change.
 */

export interface BargeInCallbacks {
  /** Fires once when RMS has stayed above bargeThreshold for confirmMs,
   * after the arm delay. Never fires more than once per instance. */
  onTrigger?: () => void;
}

export interface BargeInOptions {
  sampleIntervalMs?: number;
  echoCalibrationMs?: number;
  bargeInMargin?: number;
  confirmMs?: number;
  minArmMs?: number;
  /** Floor for bargeThreshold — pass the LISTENING VAD's last-calibrated
   * threshold so a near-silent device can't produce an absurdly low bar. */
  floorThreshold?: number;
}

const DEFAULTS: Required<Omit<BargeInOptions, "floorThreshold">> = {
  sampleIntervalMs: 50,
  echoCalibrationMs: 600,
  bargeInMargin: 2.5,
  confirmMs: 300,
  minArmMs: 500,
};

function percentile(samples: number[], p: number): number {
  if (samples.length === 0) return 0;
  const sorted = [...samples].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor(p * sorted.length));
  return sorted[idx];
}

export class BargeInDetector {
  private analyser: AnalyserNode;
  private source: MediaStreamAudioSourceNode;
  private dataArray: Float32Array;
  private intervalId: number | null = null;
  private opts: Required<Omit<BargeInOptions, "floorThreshold">> & { floorThreshold: number };
  private callbacks: BargeInCallbacks;

  private startedAtMs = 0;
  private calibrating = true;
  private calibrationSamples: number[] = [];
  private echoFloor = 0;
  private bargeThreshold = 0;
  private armed = false;
  private aboveSinceMs: number | null = null;
  private triggered = false;

  constructor(
    audioContext: AudioContext,
    stream: MediaStream,
    opts?: BargeInOptions,
    callbacks?: BargeInCallbacks
  ) {
    this.opts = { ...DEFAULTS, floorThreshold: 0, ...opts };
    this.callbacks = callbacks || {};
    this.analyser = audioContext.createAnalyser();
    this.analyser.fftSize = 1024;
    this.source = audioContext.createMediaStreamSource(stream);
    this.source.connect(this.analyser);
    this.dataArray = new Float32Array(this.analyser.fftSize);
  }

  private computeRms(): number {
    this.analyser.getFloatTimeDomainData(this.dataArray);
    let sumSquares = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sumSquares += this.dataArray[i] * this.dataArray[i];
    }
    return Math.sqrt(sumSquares / this.dataArray.length);
  }

  /** Begin echo-floor calibration, then arm after minArmMs. */
  start(): void {
    if (this.intervalId !== null) return;
    this.startedAtMs = performance.now();
    this.calibrating = true;
    this.calibrationSamples = [];
    this.armed = false;
    this.triggered = false;
    this.aboveSinceMs = null;
    this.intervalId = window.setInterval(() => this.tick(), this.opts.sampleIntervalMs);
  }

  private tick(): void {
    if (this.triggered) return;
    const rms = this.computeRms();
    const elapsed = performance.now() - this.startedAtMs;

    if (this.calibrating) {
      this.calibrationSamples.push(rms);
      if (elapsed >= this.opts.echoCalibrationMs) {
        this.echoFloor = percentile(this.calibrationSamples, 0.9);
        this.bargeThreshold = Math.max(this.echoFloor * this.opts.bargeInMargin, this.opts.floorThreshold);
        this.calibrating = false;
      }
      return; // never detect during the calibration window itself
    }

    if (!this.armed) {
      if (elapsed >= this.opts.minArmMs) {
        this.armed = true;
      } else {
        return;
      }
    }

    const now = performance.now();
    if (rms >= this.bargeThreshold) {
      if (this.aboveSinceMs === null) this.aboveSinceMs = now;
      if (now - this.aboveSinceMs >= this.opts.confirmMs) {
        this.triggered = true;
        this.callbacks.onTrigger?.();
      }
    } else {
      this.aboveSinceMs = null;
    }
  }

  stop(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  disconnect(): void {
    this.stop();
    this.source.disconnect();
    this.analyser.disconnect();
  }
}
